/**
 * Account settings.
 *
 * Structure and class names follow the prototype's `screen-settings`: a heading block, a
 * sticky `settings__nav` rail, and `settings-card` panes for Account, Notifications,
 * Privacy & security, Language & region and the Danger zone. Put the two side by side and
 * they are the same page.
 *
 * WHAT IS AND IS NOT HERE. Every control on this page writes through an endpoint the
 * platform already ships, and several of the prototype's controls are therefore absent
 * — a switch that cannot save is worse than no switch at all. In the prototype's own
 * order, and why:
 *
 *   first / last name      Open edX stores ONE `name`. Splitting it in the UI would mean
 *                          guessing where to cut a name, which is wrong for a large part
 *                          of the world before it is wrong for anyone else.
 *   headline / role        no such field on the account. Nothing to write it to.
 *   password (3 boxes)     there is no instant password-change endpoint. The only route
 *                          is an emailed link, so the card says exactly that and sends
 *                          one — see `requestPasswordReset`.
 *   two-factor auth        no learner-callable API in the platform.
 *   site language          the interface language is a real preference (`pref-lang`), but
 *                          nothing exposes the list of languages the academy has
 *                          released, and this install has released one. A picker with a
 *                          single option that we had to hardcode is not a feature.
 *   date & number format   not a stored preference. Dates follow the browser's locale.
 *   export your data       retirement/report APIs are operator-only, not learner-callable.
 *   deactivate (reversible) does not exist. Closing an account is permanent — the one
 *                          endpoint that exists is the permanent one, and it is in the
 *                          danger zone under its real name.
 *
 * The rest — photo, name, about-me, demographics, email, notification channels, profile
 * visibility, time zone, account closure — are real and save.
 *
 * Personal data: nothing on this page is logged, and no value reaches a query string.
 * The only destination for any of it is the academy's own API.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Icon, Spinner } from '../../shared/ui';
import { loadSession } from '../../shared/lib/session';
import {
  type Account,
  avatarUrl,
  checkImage,
  closeAccount,
  explainSaveFailure,
  fieldErrors,
  getAccount,
  getFieldOptions,
  getNotificationPreferences,
  getPreferences,
  getTimeZones,
  humanise,
  IMAGE_RULES,
  initialsOf,
  type NotificationChannel,
  patchAccount,
  patchPreferences,
  PasswordResetUnreachable,
  PUBLIC_PROFILE_FIELDS,
  requestEmailChange,
  requestPasswordReset,
  setNotificationChannel,
  setSocialLink,
  SOCIAL_PLATFORMS,
  socialLinkFor,
  TIME_ZONE_KEY,
  uploadProfileImage,
  removeProfileImage,
  VISIBILITY_ALL,
  VISIBILITY_PRIVATE,
  visibilityOf,
} from '../../shared/lib/account';
import { ApiError } from '../../shared/lib/openedx';
import './account.css';

type Pane = 'account' | 'notifications' | 'privacy' | 'language' | 'danger';

const PANES: { id: Pane; label: string; icon: string }[] = [
  { id: 'account', label: 'Account', icon: 'person' },
  { id: 'notifications', label: 'Notifications', icon: 'notifications' },
  { id: 'privacy', label: 'Privacy & security', icon: 'lock' },
  { id: 'language', label: 'Language & region', icon: 'language' },
  { id: 'danger', label: 'Danger zone', icon: 'warning' },
];

export default function Settings() {
  const [pane, setPane] = useState<Pane>('account');

  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username;

  const account = useQuery({
    queryKey: ['account', username],
    queryFn: () => getAccount(username as string),
    enabled: Boolean(username),
  });

  if (session.isLoading || (username && account.isLoading)) {
    return (
      <main className="settings">
        <Spinner label="Loading your settings…" />
      </main>
    );
  }

  if (!username) {
    return (
      <main className="settings">
        <Banner tone="warning">
          <strong>You are signed out.</strong> Sign in to manage your account.
        </Banner>
      </main>
    );
  }

  if (account.isError || !account.data) {
    return (
      <main className="settings">
        <Banner tone="danger">
          <strong>We could not load your settings.</strong> Nothing has been changed — this was
          only the request to read them. Reload the page to try again.
        </Banner>
      </main>
    );
  }

  return (
    <main className="settings">
      <div className="settings__head">
        <h1>Settings</h1>
        <p>Manage your account, notifications and privacy.</p>
      </div>

      <div className="settings__layout">
        <aside className="settings__side">
          <ul className="settings__nav">
            {PANES.map((entry) => (
              <li key={entry.id} className={pane === entry.id ? 'is-active' : undefined}>
                <button type="button" onClick={() => setPane(entry.id)} aria-current={pane === entry.id}>
                  <Icon name={entry.icon} />
                  {entry.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="settings__main">
          {pane === 'account' && <AccountPane username={username} account={account.data} />}
          {pane === 'notifications' && <NotificationsPane />}
          {pane === 'privacy' && <PrivacyPane username={username} account={account.data} />}
          {pane === 'language' && <LanguagePane username={username} />}
          {pane === 'danger' && <DangerPane />}
        </div>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ account ----- */

function AccountPane({ username, account }: { username: string; account: Account }) {
  return (
    <>
      <PhotoCard username={username} account={account} />
      <ProfileInformationCard username={username} account={account} />
      <EmailCard username={username} account={account} />
    </>
  );
}

/**
 * Profile photo.
 *
 * Upload and remove are separate endpoints on the same URL (POST and DELETE), and both
 * answer 204 — so the new image is picked up by refetching the account, whose
 * `profile_image` urls carry a fresh cache-busting timestamp.
 */
function PhotoCard({ username, account }: { username: string; account: Account }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const blocked = account.requires_parental_consent === true;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['account', username] });

  const upload = useMutation({
    mutationFn: (file: File) => uploadProfileImage(username, file),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => removeProfileImage(username),
    onSuccess: refresh,
  });

  const photo = avatarUrl(account);
  const busy = upload.isPending || remove.isPending;
  const error =
    localError ??
    (upload.isError ? explainSaveFailure(upload.error, 'photo') : null) ??
    (remove.isError ? explainSaveFailure(remove.error, 'photo') : null);

  return (
    <section className="settings-card">
      <h2>Profile photo</h2>
      <p className="settings-card__desc">This is the picture other learners see next to your name.</p>

      {error && <Banner tone="danger">{error}</Banner>}

      <div className="settings-avatar-row">
        <div className="avatar-lg">
          {photo ? <img src={photo} alt="" /> : initialsOf(account) || <Icon name="person" />}
        </div>
        <div>
          {blocked ? (
            <p className="settings-field__hint">
              The academy cannot accept a photo for this account until a year of birth is on file.
              Add one below and this will unlock.
            </p>
          ) : (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                className="settings-file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Reset immediately so choosing the same file twice still fires.
                  event.target.value = '';
                  if (!file) return;
                  const problem = checkImage(file);
                  setLocalError(problem);
                  if (!problem) upload.mutate(file);
                }}
              />
              <Button
                variant="secondary"
                type="button"
                busy={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                Upload new
              </Button>
              {photo && (
                <Button
                  variant="ghost"
                  type="button"
                  busy={remove.isPending}
                  disabled={busy}
                  onClick={() => {
                    setLocalError(null);
                    remove.mutate();
                  }}
                >
                  Remove
                </Button>
              )}
              <p className="settings-field__hint">{IMAGE_RULES}</p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Everything on the account record that is a plain editable field.
 *
 * One save for the whole card, because the API is a merge-patch: only the keys sent are
 * touched, so a card can own its own fields without needing to know what the rest of the
 * page holds.
 *
 * The demographic selects are built from the platform's own registration descriptor.
 * Whichever of them the operator has not enabled simply does not render — see
 * `getFieldOptions`.
 */
function ProfileInformationCard({ username, account }: { username: string; account: Account }) {
  const queryClient = useQueryClient();

  const options = useQuery({
    queryKey: ['account-field-options'],
    queryFn: getFieldOptions,
    staleTime: 60 * 60_000,
  });

  const initial = useMemo(
    () => ({
      name: account.name ?? '',
      bio: account.bio ?? '',
      country: account.country ?? '',
      level_of_education: account.level_of_education ?? '',
      gender: account.gender ?? '',
      year_of_birth: account.year_of_birth ? String(account.year_of_birth) : '',
      links: Object.fromEntries(
        SOCIAL_PLATFORMS.map((platform) => [platform.id, socialLinkFor(account, platform.id)]),
      ) as Record<string, string>,
    }),
    [account],
  );

  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [initial]);

  const save = useMutation({
    mutationFn: (patch: Partial<Account>) => patchAccount(username, patch),
    onSuccess: (updated) => queryClient.setQueryData(['account', username], updated),
  });

  const errors = fieldErrors(save.error);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const select = (field: 'level_of_education' | 'gender' | 'year_of_birth' | 'country', label: string) => {
    const list = options.data?.[field];
    if (!list) return null;
    return (
      <div className="settings-field" key={field}>
        <label htmlFor={`settings-${field}`}>{label}</label>
        <select
          id={`settings-${field}`}
          value={draft[field]}
          onChange={(event) => setDraft((d) => ({ ...d, [field]: event.target.value }))}
        >
          <option value="">Prefer not to say</option>
          {list
            // The descriptor's own blank option would duplicate ours.
            .filter(([value]) => value !== '')
            .map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
        </select>
        {errors[field] && <p className="settings-field__hint">{errors[field]}</p>}
      </div>
    );
  };

  return (
    <form
      className="settings-card"
      onSubmit={(event) => {
        event.preventDefault();
        let links = account.social_links ?? [];
        for (const platform of SOCIAL_PLATFORMS) {
          links = setSocialLink(links, platform.id, draft.links[platform.id] ?? '');
        }
        save.mutate({
          name: draft.name.trim(),
          bio: draft.bio.trim(),
          social_links: links,
          // Empty select means "no answer". The API takes null for a cleared value and
          // 400s on an empty string for the numeric one.
          ...(options.data?.country ? { country: draft.country || null } : {}),
          ...(options.data?.level_of_education
            ? { level_of_education: draft.level_of_education || null }
            : {}),
          ...(options.data?.gender ? { gender: draft.gender || null } : {}),
          ...(options.data?.year_of_birth
            ? { year_of_birth: draft.year_of_birth ? Number(draft.year_of_birth) : null }
            : {}),
        });
      }}
    >
      <h2>Profile information</h2>
      <p className="settings-card__desc">This is how you appear across the academy.</p>

      {save.isError && (
        <Banner tone="danger">
          <strong>Not saved.</strong> {explainSaveFailure(save.error, 'profile')}
        </Banner>
      )}
      {save.isSuccess && !dirty && (
        <Banner tone="success">Your profile has been saved.</Banner>
      )}

      <div className="settings-field">
        <label htmlFor="settings-username">Username</label>
        <input id="settings-username" value={account.username ?? ''} readOnly disabled />
        <p className="settings-field__hint">
          Usernames are permanent — the platform refuses to change them, because course
          records, discussion posts and certificates all point at this one.
        </p>
      </div>

      <div className="settings-field">
        <label htmlFor="settings-name">Full name</label>
        <input
          id="settings-name"
          value={draft.name}
          maxLength={255}
          autoComplete="name"
          onChange={(event) => setDraft((d) => ({ ...d, name: event.target.value }))}
        />
        <p className="settings-field__hint">
          {errors.name ??
            'One field, not two — the platform stores a single name and prints it on certificates exactly as written here.'}
        </p>
      </div>

      <div className="settings-field">
        <label htmlFor="settings-bio">About me</label>
        <textarea
          id="settings-bio"
          rows={3}
          maxLength={300}
          value={draft.bio}
          onChange={(event) => setDraft((d) => ({ ...d, bio: event.target.value }))}
        />
        <p className="settings-field__hint">{errors.bio ?? `${draft.bio.length} of 300 characters.`}</p>
      </div>

      <div className="settings-field-row">
        {select('country', 'Country')}
        {select('year_of_birth', 'Year of birth')}
      </div>
      <div className="settings-field-row">
        {select('level_of_education', 'Education')}
        {select('gender', 'Gender')}
      </div>

      {SOCIAL_PLATFORMS.map((platform) => (
        <div className="settings-field" key={platform.id}>
          <label htmlFor={`settings-${platform.id}`}>{platform.label}</label>
          <input
            id={`settings-${platform.id}`}
            type="url"
            inputMode="url"
            placeholder={platform.placeholder}
            value={draft.links[platform.id] ?? ''}
            onChange={(event) =>
              setDraft((d) => ({ ...d, links: { ...d.links, [platform.id]: event.target.value } }))
            }
          />
          {errors.social_links && <p className="settings-field__hint">{errors.social_links}</p>}
        </div>
      ))}

      <div className="settings-card__foot">
        <Button
          variant="ghost"
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => setDraft(initial)}
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit" busy={save.isPending} disabled={!dirty}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

/**
 * Email address.
 *
 * Its own card because it does not behave like the fields above: the PATCH succeeds and
 * the address does NOT change. The platform files a pending change and mails a
 * confirmation link to the new address; the account moves only when that is followed. So
 * the form clears itself and says a link is on its way, and the field above keeps showing
 * the address that is still in force.
 */
function EmailCard({ username, account }: { username: string; account: Account }) {
  const [next, setNext] = useState('');

  const request = useMutation({
    mutationFn: (email: string) => requestEmailChange(username, email),
    onSuccess: () => setNext(''),
  });

  const errors = fieldErrors(request.error);

  return (
    <form
      className="settings-card"
      onSubmit={(event) => {
        event.preventDefault();
        if (next.trim()) request.mutate(next.trim());
      }}
    >
      <h2>Email address</h2>
      <p className="settings-card__desc">
        Used to sign in, and where the academy sends course mail.
      </p>

      {request.isError && (
        <Banner tone="danger">
          <strong>Not requested.</strong> {explainSaveFailure(request.error, 'email change')}
        </Banner>
      )}
      {request.isSuccess && (
        <Banner tone="info">
          <strong>Check your new inbox.</strong> The academy has sent a confirmation link there.
          Your sign-in address stays as it is until you follow that link — so nothing has changed
          yet, and the old address still works.
        </Banner>
      )}

      <div className="settings-field">
        <label htmlFor="settings-email">Current email</label>
        <input id="settings-email" value={account.email ?? ''} readOnly disabled />
      </div>

      <div className="settings-field">
        <label htmlFor="settings-new-email">New email</label>
        <input
          id="settings-new-email"
          type="email"
          autoComplete="email"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
        <p className="settings-field__hint">
          {errors.email ?? 'Changing this takes a confirmation link sent to the new address.'}
        </p>
      </div>

      <div className="settings-card__foot">
        <Button variant="primary" type="submit" busy={request.isPending} disabled={!next.trim()}>
          Send confirmation link
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------ notifications ----- */

/**
 * Notification preferences, generated from the server's own tree.
 *
 * Nothing about which notifications exist is hardcoded here. The endpoint returns
 * apps → types → channels, and the page renders whatever it is handed, honours
 * `non_editable` per type, and hides the email column entirely when the install says
 * `show_email_preferences` is false. An operator who enables another notification app
 * gets its controls without a front-end change.
 *
 * Each toggle is its own PUT — the API has no bulk form — so there is no save button and
 * a failure names the switch that did not move.
 */
function NotificationsPane() {
  const queryClient = useQueryClient();
  const prefs = useQuery({ queryKey: ['notification-prefs'], queryFn: getNotificationPreferences });

  const [failed, setFailed] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (input: { app: string; type: string; channel: NotificationChannel; value: boolean }) =>
      setNotificationChannel(input.app, input.type, input.channel, input.value),
    onMutate: () => setFailed(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-prefs'] }),
    onError: (error) => setFailed(explainSaveFailure(error, 'notification setting')),
  });

  if (prefs.isLoading) return <Spinner label="Loading your notification preferences…" />;

  if (prefs.isError) {
    return (
      <Banner tone="danger">
        <strong>We could not load your notification preferences.</strong> They are unchanged —
        reload the page to try again.
      </Banner>
    );
  }

  if (!prefs.data?.show_preferences) {
    return (
      <section className="settings-card">
        <h2>Notification preferences</h2>
        <p className="settings-card__desc">
          This academy has not switched on configurable notifications, so there is nothing to
          choose here yet.
        </p>
      </section>
    );
  }

  const apps = Object.entries(prefs.data.data).filter(([, app]) => app.enabled);
  const channels: NotificationChannel[] = prefs.data.show_email_preferences
    ? ['web', 'email']
    : ['web'];

  if (apps.length === 0) {
    return (
      <section className="settings-card">
        <h2>Notification preferences</h2>
        <p className="settings-card__desc">No notification groups are enabled for this academy.</p>
      </section>
    );
  }

  return (
    <>
      {failed && <Banner tone="danger">{failed}</Banner>}

      {apps.map(([appKey, app]) => (
        <section className="settings-card" key={appKey}>
          <h2>{humanise(appKey)}</h2>
          <p className="settings-card__desc">
            Choose what you hear about{prefs.data.show_email_preferences ? ', and where.' : '.'}
          </p>

          {Object.entries(app.notification_types).map(([typeKey, type]) => {
            const locked = app.non_editable?.[typeKey] ?? [];
            return (
              <div className="toggle-row" key={typeKey}>
                <div className="toggle-row__body">
                  <strong>{humanise(typeKey)}</strong>
                  {type.info && <span>{type.info}</span>}
                </div>
                <div className="toggle-row__switches">
                  {channels.map((channel) => {
                    const isLocked = locked.includes(channel);
                    const on = type[channel];
                    return (
                      <label className="toggle-switch-label" key={channel}>
                        <span>{channel === 'web' ? 'In app' : 'Email'}</span>
                        <button
                          type="button"
                          className={`toggle${on ? ' is-on' : ''}`}
                          role="switch"
                          aria-checked={on}
                          aria-label={`${humanise(typeKey)} — ${channel === 'web' ? 'in app' : 'email'}`}
                          disabled={isLocked || toggle.isPending}
                          onClick={() =>
                            toggle.mutate({ app: appKey, type: typeKey, channel, value: !on })
                          }
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ privacy ----- */

function PrivacyPane({ username, account }: { username: string; account: Account }) {
  const queryClient = useQueryClient();
  const privacy = visibilityOf(account);

  const setVisibility = useMutation({
    mutationFn: (value: string) => patchAccount(username, { account_privacy: value }),
    onSuccess: (updated) => queryClient.setQueryData(['account', username], updated),
  });

  const reset = useMutation({ mutationFn: requestPasswordReset });

  return (
    <>
      <section className="settings-card">
        <h2>Password</h2>
        <p className="settings-card__desc">
          The academy does not let anything — including this page — set a password directly.
          Changing it always goes through a one-time link sent to your email address, so a
          stolen session cannot lock you out of your own account.
        </p>

        {reset.isError && (
          <Banner tone="danger">
            {reset.error instanceof PasswordResetUnreachable ? (
              <>
                <strong>The request did not reach the academy.</strong> Nothing has been sent and
                your password is unchanged. Try again in a moment, and tell the academy team if it
                keeps happening.
              </>
            ) : reset.error instanceof ApiError && reset.error.status === 403 ? (
              <>
                <strong>A link was already sent very recently.</strong> Check your inbox, including
                spam, and wait a few minutes before asking for another.
              </>
            ) : (
              explainSaveFailure(reset.error, 'password reset')
            )}
          </Banner>
        )}
        {reset.isSuccess && (
          <Banner tone="success">
            <strong>Check your email.</strong> A link to set a new password is on its way to{' '}
            the address on this account. It expires, so use it soon. Your current password keeps
            working until you do.
          </Banner>
        )}

        <div className="settings-card__foot">
          <Button variant="primary" type="button" busy={reset.isPending} onClick={() => reset.mutate()}>
            Email me a reset link
          </Button>
        </div>
      </section>

      <section className="settings-card">
        <h2>Profile visibility</h2>
        <p className="settings-card__desc">
          Controls what other learners see when they find you in a discussion or a peer review.
        </p>

        {setVisibility.isError && (
          <Banner tone="danger">
            <strong>Not saved.</strong> {explainSaveFailure(setVisibility.error, 'visibility setting')}
          </Banner>
        )}

        {privacy.forced ? (
          <Banner tone="info">
            <strong>Your profile is private and cannot be opened up yet.</strong> The academy has no
            year of birth on file for this account, so the platform keeps it private regardless of
            this setting. Add one under Account and the choice below becomes yours.
          </Banner>
        ) : (
          <div className="toggle-row">
            <div className="toggle-row__body">
              <strong>Show my profile to other learners</strong>
              <span>They see {PUBLIC_PROFILE_FIELDS}. Your name, email and grades stay private.</span>
            </div>
            <button
              type="button"
              className={`toggle${privacy.isPublic ? ' is-on' : ''}`}
              role="switch"
              aria-checked={privacy.isPublic}
              aria-label="Show my profile to other learners"
              disabled={setVisibility.isPending}
              onClick={() =>
                setVisibility.mutate(privacy.isPublic ? VISIBILITY_PRIVATE : VISIBILITY_ALL)
              }
            />
          </div>
        )}
      </section>
    </>
  );
}

/* ------------------------------------------------------- language and region ---- */

/**
 * Time zone.
 *
 * The list is the server's — 433 zones with descriptions it has already formatted — and
 * the value is saved as a PREFERENCE, not on the account. The account record carries a
 * `time_zone` too; it is always null and quietly ignores writes, which is the single
 * easiest way to build a time-zone picker that appears to work and never does.
 *
 * The prototype's site-language and date-format controls are not here — see the note at
 * the top of this file.
 */
function LanguagePane({ username }: { username: string }) {
  const queryClient = useQueryClient();

  const prefs = useQuery({
    queryKey: ['preferences', username],
    queryFn: () => getPreferences(username),
  });
  const zones = useQuery({ queryKey: ['time-zones'], queryFn: getTimeZones, staleTime: 60 * 60_000 });

  const saved = prefs.data?.[TIME_ZONE_KEY] ?? '';
  const [choice, setChoice] = useState(saved);
  useEffect(() => setChoice(saved), [saved]);

  const save = useMutation({
    mutationFn: (value: string) =>
      // Merge-patch: null deletes the preference, which is how "use my browser's zone"
      // is expressed. An empty string would be stored as an empty preference instead.
      patchPreferences(username, value ? { [TIME_ZONE_KEY]: value } : ({ [TIME_ZONE_KEY]: null } as never)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preferences', username] }),
  });

  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (prefs.isLoading || zones.isLoading) return <Spinner label="Loading your region settings…" />;

  if (prefs.isError || zones.isError) {
    return (
      <Banner tone="danger">
        <strong>We could not load your region settings.</strong> They are unchanged — reload the
        page to try again.
      </Banner>
    );
  }

  return (
    <section className="settings-card">
      <h2>Time zone</h2>
      <p className="settings-card__desc">
        Due dates, live session times and certificate dates are shown in this zone.
      </p>

      {save.isError && (
        <Banner tone="danger">
          <strong>Not saved.</strong> {explainSaveFailure(save.error, 'time zone')}
        </Banner>
      )}
      {save.isSuccess && choice === saved && <Banner tone="success">Your time zone has been saved.</Banner>}

      <div className="settings-field">
        <label htmlFor="settings-timezone">Time zone</label>
        <select
          id="settings-timezone"
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
        >
          <option value="">Follow this device ({browserZone})</option>
          {zones.data?.map((zone) => (
            <option key={zone.time_zone} value={zone.time_zone}>
              {zone.description}
            </option>
          ))}
        </select>
        <p className="settings-field__hint">
          Leave it on the first option and the academy uses whatever zone your device reports,
          which follows you when you travel.
        </p>
      </div>

      <div className="settings-card__foot">
        <Button
          variant="ghost"
          type="button"
          disabled={choice === saved || save.isPending}
          onClick={() => setChoice(saved)}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          busy={save.isPending}
          disabled={choice === saved}
          onClick={() => save.mutate(choice)}
        >
          Save preferences
        </Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- danger ----- */

/**
 * Closing the account.
 *
 * The endpoint is `deactivate_logout`, and the name is misleading: it is not a pause. It
 * makes the password unusable, ends the session and queues the account for the
 * platform's retirement pipeline, which erases the personal data. There is no undo and
 * no reversible deactivation endpoint to offer instead, so the card says so plainly and
 * asks for the password and a typed confirmation before the button does anything.
 */
function DangerPane() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const close = useMutation({
    mutationFn: () => closeAccount(password),
    onSuccess: () => {
      // The server has already ended the session. A full reload is the honest next step:
      // every cached query on this page belongs to an account that no longer exists.
      window.location.assign('/');
    },
  });

  const ready = password.length > 0 && confirm.trim().toUpperCase() === 'DELETE';

  return (
    <form
      className="settings-card settings-danger"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) close.mutate();
      }}
    >
      <h2>Delete your account</h2>
      <p className="settings-card__desc">
        This is permanent. Your password stops working immediately, and the academy then erases
        your personal details. Course records you have already earned certificates for are kept by
        the academy in anonymised form, but you will not be able to reach them, and nothing here
        can be undone or reactivated later.
      </p>

      {close.isError && (
        <Banner tone="danger">
          <strong>Your account has not been deleted.</strong>{' '}
          {close.error instanceof ApiError && close.error.status === 401
            ? 'That password was not correct. Nothing has changed.'
            : explainSaveFailure(close.error, 'deletion')}
        </Banner>
      )}

      <div className="settings-field">
        <label htmlFor="settings-close-password">Your password</label>
        <input
          id="settings-close-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>

      <div className="settings-field">
        <label htmlFor="settings-close-confirm">Type DELETE to confirm</label>
        <input
          id="settings-close-confirm"
          value={confirm}
          autoComplete="off"
          onChange={(event) => setConfirm(event.target.value)}
        />
      </div>

      <div className="settings-card__foot">
        <Button variant="danger" type="submit" busy={close.isPending} disabled={!ready}>
          Delete my account
        </Button>
      </div>
    </form>
  );
}
